/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { servicesApi, faqsApi, aboutUsApi, searchApi } from '@/api/strapi/strapi-api';
import i18next from 'i18next';

type QueryParams = Record<string, any>;

// Services hooks
export const useServices = (params: QueryParams = {}) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.list({ ...params, locale: language }),
    queryFn: () => servicesApi.getAll(params),
  });
};

export const useService = (id: number | string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.detail(id, language),
    queryFn: () => servicesApi.getById(id as string),
    enabled: !!id,
  });
};

export const useServiceSearch = (term: string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.search(term, language),
    queryFn: () => servicesApi.search(term),
    enabled: !!term && term.length > 2,
  });
};

export const useServicesByCategory = (categoryId: number | string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.byCategory(categoryId, language),
    queryFn: () => servicesApi.getByCategory(categoryId as string),
    enabled: !!categoryId,
  });
};

export const useServicesByAuthority = (authorityId: number | string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.byAuthority(authorityId, language),
    queryFn: () => servicesApi.getByAuthority(authorityId as string),
    enabled: !!authorityId,
  });
};

export const useServicesByZone = (zoneId: number | string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.services.byZone(zoneId, language),
    queryFn: () => servicesApi.getByZone(zoneId as string),
    enabled: !!zoneId,
  });
};

export const useFaqs = (params: QueryParams = {}) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.faqs.list({ ...params, locale: language }),
    queryFn: () => faqsApi.getAll(params),
  });
};

export const useFaq = (id: number | string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.faqs.detail(id, language),
    queryFn: () => faqsApi.getById(id as string),
    enabled: !!id,
  });
};

export const useAboutUs = () => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.aboutUs.list({ locale: language }),
    queryFn: () => aboutUsApi.getAll(),
  });
};

export const useSearch = (term: string) => {
  const { language } = i18next;
  return useQuery({
    queryKey: queryKeys.search.search(term, language),
    queryFn: () => searchApi.search(term),
    enabled: !!term && term.length > 2,
  });
};
